import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Skeleton, Tag, Typography, message } from "antd";
import {
    AuditOutlined,
    BranchesOutlined,
    DownloadOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    FileTextOutlined,
    LinkOutlined,
    MailOutlined,
    PaperClipOutlined,
    ThunderboltOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import {
    downloadInboxProxyContent,
    getInboxAttachmentAccess,
    getInboxRawMessageAccess,
    getMailReceiptContext,
    getMailThreadContext,
    triggerJudgment,
} from "../../../../api/v3/mailHubApi";
import {
    buildMailReadingSurfaceViewModel,
    getMailReadingSurfaceInitialReceiptId,
    isSafeInlineAttachmentPreviewContentType,
} from "../models/mailReadingSurfaceViewModel";
import { formatContractError } from "../models/contractValidation";
import BusinessObjectCard from "./BusinessObjectCard";
import "./MailReadingSurface.css";

const { Text, Title } = Typography;

function extract(response) {
    return response?.data?.data;
}

function extractErrorMessage(error, fallback) {
    return error?.response?.data?.error
        || error?.response?.data?.details
        || error?.message
        || fallback;
}

export function getJudgmentAgentLabel(category) {
    return category?.trim().toLowerCase() === "permit" ? "BJ-FP" : "BJA";
}

function buildScopeKey(threadId, receiptId) {
    return `${threadId || ""}:${receiptId || ""}`;
}

function triggerBlobOpen(blob, fileName, download) {
    const objectUrl = window.URL.createObjectURL(blob);
    if (download) {
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName || "download";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else {
        window.open(objectUrl, "_blank", "noopener,noreferrer");
    }
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 30000);
}

async function openAccessDescriptor(descriptor, { download, resourceKind = "attachment" } = {}) {
    if (!download && resourceKind === "attachment" && !isSafeInlineAttachmentPreviewContentType(descriptor?.content_type)) {
        throw new Error("该附件类型不允许在线预览，请下载后在受控环境中打开");
    }

    if (descriptor?.url) {
        window.open(descriptor.url, "_blank", "noopener,noreferrer");
        return;
    }

    if (descriptor?.content_url) {
        const response = await downloadInboxProxyContent(descriptor.content_url);
        const blob = new Blob([response.data], { type: descriptor.content_type || "application/octet-stream" });
        triggerBlobOpen(blob, descriptor.file_name, download);
        return;
    }

    throw new Error("未获得可访问的邮件对象地址");
}

export const MAIL_BODY_IFRAME_SANDBOX = "allow-popups allow-popups-to-escape-sandbox";
export const MAIL_BODY_IFRAME_CSP = "default-src 'none'; img-src 'self' https: blob: data:; style-src 'unsafe-inline'; font-src data:; base-uri 'none'; form-action 'none'; script-src 'none'; object-src 'none'; frame-src 'none'; connect-src 'none'";
const MAIL_BODY_IFRAME_CSS = `
html, body { margin: 0; padding: 0; background: #fff; color: #111827; }
body { padding: 18px; font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.55; overflow-wrap: anywhere; }
table { max-width: 100%; }
img { max-width: 100%; height: auto; }
a { color: #1d4ed8; }
blockquote { margin: 12px 0; padding-left: 12px; border-left: 3px solid #dbe7f5; color: #475569; }
pre { white-space: pre-wrap; overflow-wrap: anywhere; }
.mail-hub-image-placeholder { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 80px; height: 80px; max-width: 100%; margin: 4px 6px 4px 0; border: 1px solid #cbd5e1; border-radius: 10px; background: #f8fafc; color: #94a3b8; vertical-align: middle; box-sizing: border-box; }
.mail-hub-image-placeholder::before { content: ""; width: 32px; height: 26px; border: 2px solid currentColor; border-radius: 5px; background: linear-gradient(135deg, transparent 50%, currentColor 51%, currentColor 58%, transparent 59%), linear-gradient(45deg, transparent 56%, currentColor 57%, currentColor 64%, transparent 65%); opacity: .72; box-sizing: border-box; }
.mail-hub-image-placeholder::after { content: ""; position: absolute; top: 25px; right: 25px; width: 6px; height: 6px; border-radius: 999px; background: currentColor; opacity: .72; }
.mail-hub-image-placeholder-remote_blocked { border-color: #cbd5e1; background: #f8fafc; color: #94a3b8; }
.mail-hub-image-placeholder-unavailable { border-color: #fed7aa; background: #fff7ed; color: #c08457; }
.mail-hub-image-placeholder-upstream_placeholder { border-color: #e2e8f0; background: #f8fafc; color: #cbd5e1; }
.mail-hub-image-placeholder-tracking_pixel { display: none !important; width: 0 !important; height: 0 !important; margin: 0 !important; border: 0 !important; padding: 0 !important; }
`;

function escapeHtmlAttribute(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

export function getSafeMailBodyIframeSandbox() {
    return MAIL_BODY_IFRAME_SANDBOX;
}

export function getSafeMailBodyIframeCsp() {
    return MAIL_BODY_IFRAME_CSP;
}

export function buildMailBodyIframeSrcDoc(content) {
    return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${escapeHtmlAttribute(getSafeMailBodyIframeCsp())}"><base target="_blank"><style>${MAIL_BODY_IFRAME_CSS}</style></head><body>${content || ""}</body></html>`;
}

function createImagePlaceholder(documentValue, entry) {
    const placeholder = documentValue.createElement("span");
    const status = entry.status || "unavailable";
    const title = status === "remote_blocked"
        ? "图片因隐私保护被阻断"
        : status === "tracking_pixel"
            ? "追踪像素已过滤"
        : status === "upstream_placeholder"
            ? "邮件原文中的图片占位"
            : entry.source === "remote"
                ? "远程图片暂不可用"
            : "内嵌图片暂不可回放";
    placeholder.className = `mail-hub-image-placeholder mail-hub-image-placeholder-${status}`;
    placeholder.setAttribute("title", title);
    placeholder.setAttribute("data-mail-hub-image-status", status);
    return placeholder;
}

function hydrateMailImageResources(content, resourcePolicy) {
    const entries = resourcePolicy?.images?.entries || [];
    if (!content || entries.length === 0 || typeof DOMParser === "undefined") {
        return content;
    }

    const parser = new DOMParser();
    const documentValue = parser.parseFromString(content, "text/html");
    const images = Array.from(documentValue.querySelectorAll("img[data-mail-hub-image-ref]"));
    entries.forEach((entry) => {
        const image = images.find((candidate) => candidate.getAttribute("data-mail-hub-image-ref") === entry.imageRef);
        if (!image) return;

        if (entry.status === "tracking_pixel") {
            image.remove();
            return;
        }

        if (entry.status === "loadable") {
            if (entry.contentUrl) {
                image.setAttribute("src", entry.contentUrl);
                image.setAttribute("alt", entry.source === "remote" ? "远程图片" : "邮件内嵌图片");
                return;
            }
            image.replaceWith(createImagePlaceholder(documentValue, { ...entry, status: "unavailable", placeholderText: "内嵌图片暂不可回放" }));
            return;
        }

        image.replaceWith(createImagePlaceholder(documentValue, entry));
    });
    return documentValue.body.innerHTML;
}

function getResourceNoteCounts(resourcePolicy = {}) {
    const entries = resourcePolicy.images?.entries || [];
    const loadableCount = entries.filter((entry) => entry.status === "loadable" && entry.contentUrl).length;
    return {
        loadableTotal: loadableCount,
        loadedCount: loadableCount,
        cidUnavailableCount: entries.filter((entry) => entry.status === "unavailable" && entry.source === "cid").length,
        remoteUnavailableCount: entries.filter((entry) => entry.status === "unavailable" && entry.source === "remote").length,
        remoteBlockedCount: entries.filter((entry) => entry.status === "remote_blocked").length || resourcePolicy.remoteImages?.blockedCount || 0,
        trackingPixelCount: entries.filter((entry) => entry.status === "tracking_pixel").length || resourcePolicy.remoteImages?.trackingPixelCount || 0,
        upstreamPlaceholderCount: entries.filter((entry) => entry.status === "upstream_placeholder").length,
    };
}

function ToneTag({ status }) {
    return <span className={`mail-reading-status mail-reading-status-${status?.tone || "neutral"}`}>{status?.label}</span>;
}

export function MailBodyIframe({ content, resourcePolicy = {}, className = "" }) {
    const hydratedContent = useMemo(
        () => hydrateMailImageResources(content, resourcePolicy),
        [content, resourcePolicy],
    );
    const srcDoc = useMemo(
        () => buildMailBodyIframeSrcDoc(hydratedContent),
        [hydratedContent],
    );

    const noteCounts = getResourceNoteCounts(resourcePolicy);

    return (
        <div className={`mail-reading-body-frame-wrap ${className}`.trim()}>
            {noteCounts.loadableTotal > 0 || noteCounts.remoteBlockedCount > 0 || noteCounts.cidUnavailableCount > 0 || noteCounts.remoteUnavailableCount > 0 || noteCounts.trackingPixelCount > 0 ? (
                <div className="mail-reading-body-resource-note">
                    {noteCounts.loadableTotal > 0 ? <span>图片通过 Mail Hub 短时签名 URL 回放：{`${noteCounts.loadedCount}/${noteCounts.loadableTotal}`}</span> : null}
                    {noteCounts.cidUnavailableCount > 0 ? <span>{noteCounts.cidUnavailableCount} 个内嵌图片暂不可回放</span> : null}
                    {noteCounts.remoteUnavailableCount > 0 ? <span>{noteCounts.remoteUnavailableCount} 个远程图片暂不可用</span> : null}
                    {noteCounts.trackingPixelCount > 0 ? <span>{noteCounts.trackingPixelCount} 个追踪像素已被过滤</span> : null}
                    {noteCounts.remoteBlockedCount > 0 ? <span>因隐私保护阻断 {noteCounts.remoteBlockedCount} 个远程图片</span> : null}
                </div>
            ) : null}
            <iframe
                title="邮件 HTML 正文"
                className="mail-reading-body-frame"
                sandbox={getSafeMailBodyIframeSandbox()}
                referrerPolicy="no-referrer"
                srcDoc={srcDoc}
            />
        </div>
    );
}

function BodyContent({ format, content, security, resourcePolicy, className = "" }) {
    if (!content) return null;

    if (format === "markdown") {
        return (
            <div className={`mail-reading-body-markdown ${className}`.trim()}>
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{content}</ReactMarkdown>
            </div>
        );
    }

    if (format === "html") {
        return <MailBodyIframe content={content} security={security} resourcePolicy={resourcePolicy} className={className} />;
    }

    return <pre className={`mail-reading-body-text ${className}`.trim()}>{content}</pre>;
}

function BodySemanticPanel({ body, quotedHistoryVisible, onToggleQuotedHistory }) {
    const segments = body?.segments || [];
    if (!segments.length) return null;
    const firstQuotedSegmentKey = segments.find((segment) => segment.isQuotedHistory)?.key || "";

    return (
        <div className="mail-reading-semantic-panel">
            <div className="mail-reading-semantic-panel-head">
                <div>
                    <Text strong>正文语义分段</Text>
                    <Text type="secondary">验证态标注，不决定上方正文展示资产。</Text>
                </div>
                {body.hasQuotedHistory ? (
                    <Button
                        size="small"
                        icon={quotedHistoryVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        onClick={onToggleQuotedHistory}
                    >
                        {quotedHistoryVisible ? "隐藏分段文本" : "显示分段文本"}
                    </Button>
                ) : null}
            </div>

            {body.hasQuotedHistory ? (
                segments.map((segment) => (
                    <React.Fragment key={segment.key}>
                        {segment.isQuotedHistory && segment.key === firstQuotedSegmentKey ? (
                        <div className="mail-reading-quote-boundary">
                            <div className="mail-reading-quote-boundary-main">
                                <span className="mail-reading-quote-title">系统识别到历史引用</span>
                                <span className="mail-reading-quote-meta">
                                    {segment.boundarySourceLabel} · {segment.confidenceText} · {segment.sourceAssetLabel}
                                </span>
                            </div>
                        </div>
                        ) : null}

                        {quotedHistoryVisible ? (
                            <div className={`mail-reading-semantic-segment mail-reading-body-segment-${segment.kind}`}>
                                <div className="mail-reading-semantic-segment-label">{segment.label}</div>
                                <pre>{segment.content}</pre>
                            </div>
                        ) : null}
                    </React.Fragment>
                ))
            ) : (
                <Text type="secondary">未识别到历史引用边界。</Text>
            )}
        </div>
    );
}

function BodyRenderableView({ body, quotedHistoryVisible, onToggleQuotedHistory }) {
    if (!body?.content) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可展示的正文内容" />;
    }

    return (
        <>
            <div className="mail-reading-body-card">
                <div className="mail-reading-render-policy">
                    <span>展示格式：{body.format === "html" ? "HTML 隔离渲染" : body.format === "markdown" ? "Markdown 回退" : "纯文本"}</span>
                    <span>来源：{body.sourceAssetLabel}</span>
                    {body.security?.sanitizerVersion ? <span>策略：{body.security.sanitizerVersion}</span> : null}
                    {body.degradation?.degraded ? <span>降级：{body.degradation.reason || "已回退"}</span> : null}
                </div>
                <BodyContent
                    format={body.format}
                    content={body.content}
                    security={body.security}
                    resourcePolicy={body.resourcePolicy}
                />
            </div>
            <BodySemanticPanel
                body={body}
                quotedHistoryVisible={quotedHistoryVisible}
                onToggleQuotedHistory={onToggleQuotedHistory}
            />
        </>
    );
}

function Timeline({ messages, onFocusReceipt }) {
    if (!messages.length) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无可展示的会话时间线" />;
    }

    return (
        <ol className="mail-reading-timeline-list">
            {messages.map((item) => (
                <li key={item.key} className="mail-reading-timeline-item-wrap">
                    <button
                        type="button"
                        className={`mail-reading-timeline-item${item.isFocused ? " mail-reading-timeline-item-focused" : ""}`}
                        onClick={() => item.receiptId && onFocusReceipt(item.receiptId)}
                    >
                        <span className="mail-reading-timeline-dot" aria-hidden="true" />
                        <span className="mail-reading-timeline-content">
                            <span className="mail-reading-timeline-meta">
                                <strong>{item.sender.label}</strong>
                                <span title={item.time.absoluteText}>{item.time.absoluteText}</span>
                            </span>
                            <span className="mail-reading-timeline-recipient">收件人：{item.recipientsText}</span>
                            {item.summary ? (
                                <span className="mail-reading-timeline-summary">{item.summary}</span>
                            ) : (
                                <span className="mail-reading-timeline-summary mail-reading-muted">暂无摘要</span>
                            )}
                            <span className="mail-reading-timeline-foot">
                                <PaperClipOutlined aria-hidden="true" />
                                <span>{item.attachmentCount > 0 ? `${item.attachmentCount} 个附件` : "无附件"}</span>
                            </span>
                        </span>
                    </button>
                </li>
            ))}
        </ol>
    );
}

function BusinessSidebar({ sidebar }) {
    const confirmedLinks = sidebar?.confirmedLinks || [];
    const pendingReviews = sidebar?.pendingReviews || [];

    return (
        <aside className="mail-reading-sidebar" aria-label="业务关联侧栏">
            <section className="mail-reading-section mail-reading-sidebar-section">
                <div className="mail-reading-section-heading">
                    <LinkOutlined />
                    <Text strong>已确认关联</Text>
                </div>

                {confirmedLinks.length > 0 ? (
                    <div className="mail-reading-business-list">
                        {confirmedLinks.map((item) => (
                            <BusinessObjectCard
                                key={item.key}
                                entityType={item.entityType}
                                entityId={item.entityId}
                                snapshot={item.snapshot}
                                links={item.links}
                                status={item.status}
                            />
                        ))}
                    </div>
                ) : (
                    <Text type="secondary">暂无已确认业务关联。</Text>
                )}
            </section>

            <section className="mail-reading-section mail-reading-sidebar-section">
                <div className="mail-reading-section-heading">
                    <AuditOutlined />
                    <Text strong>AG 待审核项</Text>
                </div>
                {pendingReviews.length > 0 ? (
                    <div className="mail-reading-review-list">
                        {pendingReviews.map((item) => (
                            <article key={item.key} className="mail-reading-review-card">
                                <div className="mail-reading-review-header">
                                    <ToneTag status={item.status} />
                                    {item.reviewCaseId ? <Text code>{item.reviewCaseId}</Text> : <Text type="secondary">审核编号受限</Text>}
                                </div>
                                <Text className="mail-reading-review-summary">{item.summary}</Text>
                                <div className="mail-reading-review-source-list">
                                    <Text type="secondary">来源 WorkItem：</Text>
                                    {item.sourceStatuses.map((status) => <ToneTag key={status.label} status={status} />)}
                                </div>
                                <div className="mail-reading-review-meta">
                                    <Text type="secondary">{item.updatedTime?.absoluteText || "审核时间受限"}</Text>
                                    {item.canOpen ? (
                                        <Link className="mail-reading-review-link" to={item.route}>
                                            打开审核工作台
                                        </Link>
                                    ) : null}
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <Text type="secondary">暂无 AG 待审核项。</Text>
                )}
            </section>
        </aside>
    );
}

/**
 * 统一邮件阅读面。
 * Props: { entry: "inbox" | "business_object" | "review", threadId, receiptId, focusEntity, reviewCaseId }
 */
export default function MailReadingSurface({
    entry = "inbox",
    threadId = "",
    receiptId = "",
    focusEntity = null,
    reviewCaseId = "",
    className = "",
}) {
    const scopeKey = buildScopeKey(threadId, receiptId);
    const [manualFocus, setManualFocus] = useState({ scopeKey: "", receiptId: "" });
    const [accessLoadingKey, setAccessLoadingKey] = useState("");
    const [quotedHistoryVisible, setQuotedHistoryVisible] = useState(true);
    const [dataState, setDataState] = useState({
        loading: false,
        error: "",
        threadContext: null,
        receiptContext: null,
        resolvedReceiptId: "",
    });

    const scopedManualReceiptId = manualFocus.scopeKey === scopeKey ? manualFocus.receiptId : "";
    const requestedReceiptId = scopedManualReceiptId || receiptId || "";

    useEffect(() => {
        let cancelled = false;

        async function loadContext() {
            if (!threadId && !requestedReceiptId) {
                setDataState({ loading: false, error: "", threadContext: null, receiptContext: null, resolvedReceiptId: "" });
                return;
            }

            setDataState({ loading: true, error: "", threadContext: null, receiptContext: null, resolvedReceiptId: requestedReceiptId });

            try {
                let threadContext = null;
                let receiptContext = null;
                let resolvedReceiptId = requestedReceiptId;

                if (threadId && requestedReceiptId) {
                    const [threadResponse, receiptResponse] = await Promise.all([
                        getMailThreadContext(threadId),
                        getMailReceiptContext(requestedReceiptId),
                    ]);
                    threadContext = extract(threadResponse);
                    receiptContext = extract(receiptResponse);
                } else if (threadId) {
                    const threadResponse = await getMailThreadContext(threadId);
                    threadContext = extract(threadResponse);
                    resolvedReceiptId = getMailReadingSurfaceInitialReceiptId(threadContext);
                    if (resolvedReceiptId) {
                        const receiptResponse = await getMailReceiptContext(resolvedReceiptId);
                        receiptContext = extract(receiptResponse);
                    }
                } else if (requestedReceiptId) {
                    const receiptResponse = await getMailReceiptContext(requestedReceiptId);
                    receiptContext = extract(receiptResponse);
                }

                const resolvedThreadId = threadId || receiptContext?.canonical_message?.thread_id || receiptContext?.thread?.id || "";
                if (!threadContext && resolvedThreadId) {
                    const threadResponse = await getMailThreadContext(resolvedThreadId);
                    threadContext = extract(threadResponse);
                }

                if (!cancelled) {
                    setDataState({
                        loading: false,
                        error: "",
                        threadContext,
                        receiptContext,
                        resolvedReceiptId,
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    setDataState({
                        loading: false,
                        error: formatContractError(error, extractErrorMessage(error, "加载邮件阅读上下文失败")),
                        threadContext: null,
                        receiptContext: null,
                        resolvedReceiptId: requestedReceiptId,
                    });
                }
            }
        }

        loadContext();

        return () => {
            cancelled = true;
        };
    }, [threadId, requestedReceiptId]);

    const viewModelState = useMemo(() => {
        try {
            return {
                contractError: "",
                viewModel: buildMailReadingSurfaceViewModel({
                    entry,
                    threadContext: dataState.threadContext,
                    receiptContext: dataState.receiptContext,
                    threadId,
                    receiptId: dataState.resolvedReceiptId || requestedReceiptId,
                    focusEntity,
                    reviewCaseId,
                }),
            };
        } catch (error) {
            return {
                contractError: formatContractError(error, "邮件阅读上下文契约错误"),
                viewModel: null,
            };
        }
    }, [dataState.receiptContext, dataState.resolvedReceiptId, dataState.threadContext, entry, focusEntity, requestedReceiptId, reviewCaseId, threadId]);
    const viewModel = viewModelState.viewModel;
    const judgmentAgentLabel = getJudgmentAgentLabel(viewModel?.classification.active.category);

    useEffect(() => {
        setQuotedHistoryVisible(true);
    }, [viewModel?.ids.receiptId]);

    const handleFocusReceipt = useCallback((nextReceiptId) => {
        setManualFocus({ scopeKey, receiptId: nextReceiptId });
    }, [scopeKey]);

    const handleToggleQuotedHistory = useCallback(() => {
        setQuotedHistoryVisible((visible) => !visible);
    }, []);

    const handleRawAccess = useCallback(async (disposition) => {
        if (!viewModel?.rawMessage.receiptId) return;
        const loadingKey = `raw:${disposition}`;
        setAccessLoadingKey(loadingKey);
        try {
            const response = await getInboxRawMessageAccess(viewModel.rawMessage.receiptId, disposition);
            await openAccessDescriptor(extract(response), { download: disposition === "attachment", resourceKind: "raw" });
        } catch (error) {
            message.error(extractErrorMessage(error, "原始邮件暂时无法访问"));
        } finally {
            setAccessLoadingKey("");
        }
    }, [viewModel?.rawMessage.receiptId]);

    const handleTriggerJudgment = useCallback(async () => {
        if (!viewModel?.ids.receiptId) return;
        setAccessLoadingKey("trigger-judgment");
        try {
            const response = await triggerJudgment(viewModel.ids.receiptId);
            const result = extract(response);
            const triggeredAgentLabel = result?.agent_slug === "business-judgment-fp" ? "BJ-FP" : judgmentAgentLabel;
            message.success(`${triggeredAgentLabel} 研判已触发，WorkItem ID: ${result.work_item_id}`);
        } catch (error) {
            message.error(extractErrorMessage(error, `触发 ${judgmentAgentLabel} 研判失败`));
        } finally {
            setAccessLoadingKey("");
        }
    }, [judgmentAgentLabel, viewModel?.ids.receiptId]);

    const handleAttachmentAccess = useCallback(async (attachment, disposition) => {
        if (disposition === "inline" && !isSafeInlineAttachmentPreviewContentType(attachment.contentType)) {
            message.warning("该附件类型不允许在线预览，请下载后在受控环境中打开");
            return;
        }

        const loadingKey = `${attachment.id}:${disposition}`;
        setAccessLoadingKey(loadingKey);
        try {
            const response = await getInboxAttachmentAccess(attachment.receiptId, attachment.id, disposition);
            await openAccessDescriptor(extract(response), { download: disposition === "attachment", resourceKind: "attachment" });
        } catch (error) {
            message.error(extractErrorMessage(error, "附件暂时无法访问"));
        } finally {
            setAccessLoadingKey("");
        }
    }, []);

    if (!threadId && !receiptId) {
        return (
            <div className={`mail-reading-surface ${className}`.trim()}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="选择一封邮件查看详情" />
            </div>
        );
    }

    if (dataState.loading) {
        return (
            <div className={`mail-reading-surface ${className}`.trim()}>
                <Skeleton active paragraph={{ rows: 10 }} />
            </div>
        );
    }

    if (dataState.error) {
        return (
            <div className={`mail-reading-surface ${className}`.trim()}>
                <Alert type="error" showIcon message="邮件阅读面加载失败" description={dataState.error} />
            </div>
        );
    }

    if (viewModelState.contractError) {
        return (
            <div className={`mail-reading-surface ${className}`.trim()}>
                <Alert type="error" showIcon message="邮件阅读面契约错误" description={viewModelState.contractError} />
            </div>
        );
    }

    return (
        <article className={`mail-reading-surface ${className}`.trim()} aria-label="邮件阅读面">
            <header className="mail-reading-header">
                <div className="mail-reading-entry-row">
                    <Tag color="blue">{viewModel.entry.label}</Tag>
                    {viewModel.classification.active.visible ? (
                        <Tag color="geekblue" title={viewModel.classification.active.codeLabel}>
                            分类：{viewModel.classification.active.label}
                        </Tag>
                    ) : null}
                    {viewModel.ids.threadId ? <Text type="secondary">Thread 时间线</Text> : <Text type="secondary">单封邮件</Text>}
                    {viewModel.classification.active.supplierName ? <Text type="secondary">供应商：{viewModel.classification.active.supplierName}</Text> : null}
                </div>
                <Title level={3} className="mail-reading-title">{viewModel.header.subject}</Title>
                <div className="mail-reading-stat-row">
                    <span><MailOutlined /> {viewModel.header.messageCount} 封邮件</span>
                    <span><UserOutlined /> {viewModel.header.participantCount} 位参与方</span>
                </div>
            </header>

            <div className="mail-reading-layout">
                <main className="mail-reading-main">
                    <section className="mail-reading-section">
                        <div className="mail-reading-section-heading">
                            <BranchesOutlined />
                            <Text strong>Thread 时间线</Text>
                        </div>
                        <Timeline messages={viewModel.thread.messages} onFocusReceipt={handleFocusReceipt} />
                    </section>

                    <section className="mail-reading-section">
                        <div className="mail-reading-section-heading mail-reading-section-heading-split">
                            <span>
                                <FileTextOutlined />
                                <Text strong>邮件正文</Text>
                            </span>
                            {viewModel.currentMessage ? <Text type="secondary">{viewModel.currentMessage.time.absoluteText}</Text> : null}
                        </div>

                        <BodyRenderableView
                            body={viewModel.body}
                            quotedHistoryVisible={quotedHistoryVisible}
                            onToggleQuotedHistory={handleToggleQuotedHistory}
                        />

                        <div className="mail-reading-raw-actions">
                            <Button
                                icon={<EyeOutlined />}
                                disabled={!viewModel.rawMessage.available}
                                loading={accessLoadingKey === "raw:inline"}
                                onClick={() => handleRawAccess("inline")}
                            >
                                查看原始邮件
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                disabled={!viewModel.rawMessage.available}
                                loading={accessLoadingKey === "raw:attachment"}
                                onClick={() => handleRawAccess("attachment")}
                            >
                                下载原始邮件
                            </Button>
                            <Button
                                icon={<ThunderboltOutlined />}
                                loading={accessLoadingKey === "trigger-judgment"}
                                onClick={handleTriggerJudgment}
                            >
                                触发 {judgmentAgentLabel} 研判
                            </Button>
                        </div>
                    </section>

                    <section className="mail-reading-section">
                        <div className="mail-reading-section-heading">
                            <PaperClipOutlined />
                            <Text strong>附件与原文</Text>
                        </div>

                        {viewModel.attachments.length > 0 ? (
                            <div className="mail-reading-attachment-list">
                                {viewModel.attachments.map((attachment) => (
                                    <article key={attachment.id} className="mail-reading-attachment-card">
                                        <div className="mail-reading-attachment-meta">
                                            <Text strong>{attachment.filename}</Text>
                                            <Text type="secondary">{attachment.sizeText} · {attachment.contentType}</Text>
                                        </div>
                                        <div className="mail-reading-attachment-actions">
                                            <Button
                                                icon={<EyeOutlined />}
                                                disabled={!attachment.canPreview}
                                                loading={accessLoadingKey === `${attachment.id}:inline`}
                                                onClick={() => handleAttachmentAccess(attachment, "inline")}
                                            >
                                                预览
                                            </Button>
                                            <Button
                                                icon={<DownloadOutlined />}
                                                loading={accessLoadingKey === `${attachment.id}:attachment`}
                                                onClick={() => handleAttachmentAccess(attachment, "attachment")}
                                            >
                                                下载
                                            </Button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        ) : (
                            <Text type="secondary">当前邮件没有附件。</Text>
                        )}
                    </section>
                </main>

                <BusinessSidebar sidebar={viewModel.business.sidebar} />
            </div>
        </article>
    );
}
